import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";

type FpmSettings = {
  pm: "ondemand" | "static" | "dynamic";
  maxChildren: number;
  startServers: number;
  minSpareServers: number;
  maxSpareServers: number;
  maxRequests: number;
  processIdleTimeout: number;
  requestTerminateTimeout: number;
  rlimitFiles: number | null;
  rlimitCore: string;
  listenBacklog: string;
  runAsNobody: boolean;
  ini: {
    allowUrlFopen: boolean;
    logErrors: boolean;
    shortOpenTag: boolean;
    disableFunctions: string;
    errorReporting: string;
  };
  block: {
    allowUrlFopen: boolean;
    logErrors: boolean;
    shortOpenTag: boolean;
    disableFunctions: boolean;
    errorReporting: boolean;
  };
};

type VerOpt = {
  version: string;
  available?: boolean;
  default?: boolean;
  label?: string;
};

const emptySettings = (): FpmSettings => ({
  pm: "ondemand",
  maxChildren: 30,
  startServers: 0,
  minSpareServers: 10,
  maxSpareServers: 20,
  maxRequests: 40,
  processIdleTimeout: 40,
  requestTerminateTimeout: 140,
  rlimitFiles: 131072,
  rlimitCore: "",
  listenBacklog: "",
  runAsNobody: false,
  ini: {
    allowUrlFopen: true,
    logErrors: true,
    shortOpenTag: true,
    disableFunctions: "",
    errorReporting: "E_ALL & ~E_NOTICE"
  },
  block: {
    allowUrlFopen: false,
    logErrors: false,
    shortOpenTag: false,
    disableFunctions: false,
    errorReporting: false
  }
});

type Props = {
  initialUsername?: string;
  initialVersion?: string;
  onBack?: () => void;
};

type NavId =
  | "general"
  | "process"
  | "timeouts"
  | "limits"
  | "security"
  | "ini"
  | "advanced";

/**
 * Premium PHP-FPM configuration workspace (global or per-user).
 */
export function PhpFpmSettingsPage({
  initialUsername = "",
  initialVersion = "",
  onBack
}: Props) {
  const [username, setUsername] = useState(initialUsername);
  const [version, setVersion] = useState(initialVersion);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [versions, setVersions] = useState<VerOpt[]>([]);
  const [settings, setSettings] = useState<FpmSettings>(emptySettings());
  const [baseline, setBaseline] = useState<string>("");
  const [source, setSource] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [nav, setNav] = useState<NavId>("general");

  const isCustom = Boolean(username);
  const dirty = loaded && JSON.stringify(settings) !== baseline;

  const load = useCallback(
    async (u?: string, v?: string) => {
      setError("");
      try {
        const qs = new URLSearchParams();
        const uu = u !== undefined ? u : username;
        const vv = v !== undefined ? v : version;
        if (uu) qs.set("username", uu);
        if (vv) qs.set("version", vv);
        const q = qs.toString() ? `?${qs}` : "";
        const res = await api<{
          username?: string;
          version?: string;
          settings?: FpmSettings;
          source?: string;
          versions?: VerOpt[];
          usernames?: string[];
        }>(`/api/v1/php/fpm-settings${q}`);
        if (res.versions) setVersions(res.versions);
        if (res.usernames) setUsernames(res.usernames);
        if (res.version) setVersion(res.version);
        if (res.username !== undefined && u !== undefined) {
          setUsername(res.username);
        }
        const next = res.settings
          ? {
              ...emptySettings(),
              ...res.settings,
              ini: { ...emptySettings().ini, ...(res.settings.ini || {}) },
              block: {
                ...emptySettings().block,
                ...(res.settings.block || {})
              }
            }
          : emptySettings();
        setSettings(next);
        setBaseline(JSON.stringify(next));
        setSource(res.source || "");
        setLoaded(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [username, version]
  );

  useEffect(() => {
    void load(initialUsername, initialVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = <K extends keyof FpmSettings>(
    key: K,
    value: FpmSettings[K]
  ) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const patchIni = <K extends keyof FpmSettings["ini"]>(
    key: K,
    value: FpmSettings["ini"][K]
  ) => {
    setSettings((s) => ({ ...s, ini: { ...s.ini, [key]: value } }));
  };

  const patchBlock = <K extends keyof FpmSettings["block"]>(
    key: K,
    value: boolean
  ) => {
    setSettings((s) => ({ ...s, block: { ...s.block, [key]: value } }));
  };

  const changeCount = useMemo(() => {
    if (!baseline) return 0;
    try {
      const a = JSON.parse(baseline) as FpmSettings;
      let n = 0;
      const keys: (keyof FpmSettings)[] = [
        "pm",
        "maxChildren",
        "startServers",
        "minSpareServers",
        "maxSpareServers",
        "maxRequests",
        "processIdleTimeout",
        "requestTerminateTimeout",
        "rlimitFiles",
        "rlimitCore",
        "listenBacklog",
        "runAsNobody"
      ];
      for (const k of keys) {
        if (JSON.stringify(a[k]) !== JSON.stringify(settings[k])) n++;
      }
      for (const k of Object.keys(settings.ini) as (keyof FpmSettings["ini"])[]) {
        if (a.ini[k] !== settings.ini[k]) n++;
      }
      for (const k of Object.keys(
        settings.block
      ) as (keyof FpmSettings["block"])[]) {
        if (a.block[k] !== settings.block[k]) n++;
      }
      return n;
    } catch {
      return dirty ? 1 : 0;
    }
  }, [baseline, settings, dirty]);

  const submit = async () => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/php/fpm-settings", {
        method: "PUT",
        body: JSON.stringify({
          username: username || undefined,
          version,
          settings
        })
      });
      setMsg(
        username
          ? `PHP-FPM settings saved for ${username}`
          : `Global PHP-FPM settings saved for PHP ${version}`
      );
      await load(username, version);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (
      !confirm(
        username
          ? `Reset custom FPM settings for ${username}? Global defaults will apply.`
          : `Reset global FPM settings for PHP ${version} to RealPanel defaults?`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/php/fpm-settings", {
        method: "PUT",
        body: JSON.stringify({
          username: username || undefined,
          version,
          reset: true
        })
      });
      setMsg("Configuration reset");
      await load(username, version);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const navItems: { id: NavId; label: string }[] = [
    { id: "general", label: "General" },
    { id: "process", label: "Process Manager" },
    { id: "timeouts", label: "Timeouts" },
    { id: "limits", label: "File Limits" },
    { id: "security", label: "Security" },
    { id: "ini", label: "PHP INI" },
    { id: "advanced", label: "Advanced" }
  ];

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">
            {isCustom ? "PHP-FPM Settings" : "Global PHP-FPM Settings"}
          </h1>
          <p className="rp-page-sub">
            {isCustom
              ? `Custom PHP-FPM configuration for ${username}`
              : "Default PHP-FPM behavior for domains without custom settings."}
          </p>
        </div>
        <div className="rp-page-actions">
          {onBack ? (
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              ← Back
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => void reset()}
          >
            Reset to Defaults
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            disabled={busy || !dirty}
            onClick={() => void submit()}
          >
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {/* Scope indicator */}
      <div className="fpm-scope">
        <div>
          <strong>
            Configuration Scope · {isCustom ? "Custom" : "Global"}
          </strong>
          <p>
            {isCustom
              ? "Overrides the global configuration for this user/domain."
              : "Applies to all users/domains without custom PHP-FPM settings."}
            {source ? ` · Source: ${source}` : ""}
          </p>
        </div>
        <span
          className={`rp-status ${
            isCustom ? "rp-status-info" : "rp-status-ok"
          }`}
        >
          <span className="rp-status-dot" />
          {isCustom ? "Custom profile" : "Global configuration active"}
        </span>
      </div>

      <div className="fpm-info-banner">
        <span className="fpm-info-ico" aria-hidden>
          i
        </span>
        <div>
          <strong>
            {isCustom ? "Custom configuration" : "Global configuration"}
          </strong>
          <span>
            {isCustom
              ? "These settings override global defaults for this account only."
              : "These settings are inherited by domains that do not have custom PHP-FPM settings."}
          </span>
        </div>
      </div>

      <div className="fpm-workspace">
        <nav className="fpm-nav" aria-label="PHP-FPM sections">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`fpm-nav-item${nav === item.id ? " is-active" : ""}`}
              onClick={() => setNav(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="fpm-workspace-body">
          {!loaded ? (
            <p className="faint">Loading configuration…</p>
          ) : (
            <>
              {nav === "general" ? (
                <section className="fpm-sec">
                  <h3>Runtime</h3>
                  <p className="fpm-sec-help">
                    PHP version and process manager strategy for this scope.
                  </p>
                  <div className="rp-wiz-grid">
                    <label className="field">
                      <span>Username (scope)</span>
                      <select
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          void load(e.target.value, version);
                        }}
                        disabled={busy}
                      >
                        <option value="">— Global (all users) —</option>
                        {usernames.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>PHP Version</span>
                      <select
                        value={version}
                        onChange={(e) => {
                          setVersion(e.target.value);
                          void load(username, e.target.value);
                        }}
                        disabled={busy}
                      >
                        {versions.length === 0 ? (
                          <option value={version}>PHP {version}</option>
                        ) : (
                          versions.map((v) => (
                            <option key={v.version} value={v.version}>
                              {v.label ||
                                `PHP ${v.version}${
                                  v.default ? " (default)" : ""
                                }`}
                            </option>
                          ))
                        )}
                      </select>
                    </label>
                    <label className="field full">
                      <span>PHP-FPM process manager</span>
                      <select
                        value={settings.pm}
                        onChange={(e) =>
                          patch("pm", e.target.value as FpmSettings["pm"])
                        }
                        disabled={busy}
                      >
                        <option value="ondemand">Ondemand</option>
                        <option value="static">Static</option>
                        <option value="dynamic">Dynamic</option>
                      </select>
                    </label>
                  </div>
                </section>
              ) : null}

              {nav === "process" ? (
                <section className="fpm-sec">
                  <h3>Process Manager</h3>
                  <p className="fpm-sec-help">
                    Controls how PHP-FPM creates and recycles worker processes.
                  </p>
                  <div className="rp-wiz-grid">
                    <NumField
                      label="Max Children"
                      value={settings.maxChildren}
                      onChange={(n) => patch("maxChildren", n)}
                      disabled={busy}
                    />
                    <NumField
                      label="Start Servers"
                      value={settings.startServers}
                      onChange={(n) => patch("startServers", n)}
                      disabled={busy || settings.pm === "ondemand"}
                    />
                    <NumField
                      label="Min Spare Servers"
                      value={settings.minSpareServers}
                      onChange={(n) => patch("minSpareServers", n)}
                      disabled={busy || settings.pm !== "dynamic"}
                    />
                    <NumField
                      label="Max Spare Servers"
                      value={settings.maxSpareServers}
                      onChange={(n) => patch("maxSpareServers", n)}
                      disabled={busy || settings.pm !== "dynamic"}
                    />
                    <NumField
                      label="Max Requests"
                      value={settings.maxRequests}
                      onChange={(n) => patch("maxRequests", n)}
                      disabled={busy}
                    />
                  </div>
                </section>
              ) : null}

              {nav === "timeouts" ? (
                <section className="fpm-sec">
                  <h3>Timeouts</h3>
                  <p className="fpm-sec-help">
                    How long idle workers live and how long a request may run.
                  </p>
                  <div className="rp-wiz-grid">
                    <NumField
                      label="Process Idle Timeout (seconds)"
                      value={settings.processIdleTimeout}
                      onChange={(n) => patch("processIdleTimeout", n)}
                      disabled={busy}
                    />
                    <NumField
                      label="Request Terminate Timeout (seconds)"
                      value={settings.requestTerminateTimeout}
                      onChange={(n) => patch("requestTerminateTimeout", n)}
                      disabled={busy}
                    />
                  </div>
                </section>
              ) : null}

              {nav === "limits" ? (
                <section className="fpm-sec">
                  <h3>File &amp; Connection Limits</h3>
                  <p className="fpm-sec-help">
                    OS-level descriptors and listen backlog for the pool.
                  </p>
                  <div className="rp-wiz-grid">
                    <NumField
                      label="File Limit (rlimit_files)"
                      value={settings.rlimitFiles ?? 0}
                      onChange={(n) => patch("rlimitFiles", n || null)}
                      disabled={busy}
                    />
                    <label className="field">
                      <span>Listen Backlog</span>
                      <input
                        type="text"
                        value={settings.listenBacklog}
                        onChange={(e) => patch("listenBacklog", e.target.value)}
                        disabled={busy}
                        placeholder="Default: unlimited"
                      />
                    </label>
                    <label className="field full">
                      <span>Ulimit / rlimit_core</span>
                      <input
                        type="text"
                        value={settings.rlimitCore}
                        onChange={(e) => patch("rlimitCore", e.target.value)}
                        disabled={busy}
                      />
                    </label>
                  </div>
                </section>
              ) : null}

              {nav === "security" || nav === "ini" ? (
                <section className="fpm-sec">
                  <h3>PHP INI</h3>
                  <p className="fpm-sec-help">
                    Pool-level PHP directives. Use “Allow domain override” to
                    permit per-site changes.
                  </p>
                  <div className="fpm-ini-rows">
                    <IniToggle
                      label="allow_url_fopen"
                      desc="Treat URLs as files"
                      checked={settings.ini.allowUrlFopen}
                      onChecked={(v) => patchIni("allowUrlFopen", v)}
                      allowOverride={!settings.block.allowUrlFopen}
                      onAllowOverride={(v) => patchBlock("allowUrlFopen", !v)}
                      disabled={busy}
                    />
                    <IniToggle
                      label="log_errors"
                      desc="Log errors to the error log"
                      checked={settings.ini.logErrors}
                      onChecked={(v) => patchIni("logErrors", v)}
                      allowOverride={!settings.block.logErrors}
                      onAllowOverride={(v) => patchBlock("logErrors", !v)}
                      disabled={busy}
                    />
                    <IniToggle
                      label="short_open_tag"
                      desc="Recognize short PHP tags"
                      checked={settings.ini.shortOpenTag}
                      onChecked={(v) => patchIni("shortOpenTag", v)}
                      allowOverride={!settings.block.shortOpenTag}
                      onAllowOverride={(v) => patchBlock("shortOpenTag", !v)}
                      disabled={busy}
                    />
                  </div>
                  <div className="field" style={{ marginTop: 18 }}>
                    <label>
                      Disabled PHP Functions{" "}
                      <span className="rp-status rp-status-crit">Security</span>
                    </label>
                    <textarea
                      rows={3}
                      value={settings.ini.disableFunctions}
                      onChange={(e) =>
                        patchIni("disableFunctions", e.target.value)
                      }
                      disabled={busy}
                      placeholder="exec,passthru,shell_exec,system,…"
                    />
                    <p className="card-meta" style={{ marginTop: 6 }}>
                      Functions listed here cannot be executed by PHP.
                    </p>
                    <label className="rp-check" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={!settings.block.disableFunctions}
                        onChange={(e) =>
                          patchBlock("disableFunctions", !e.target.checked)
                        }
                        disabled={busy}
                      />
                      <span>Allow domain override</span>
                    </label>
                  </div>
                  <div className="field" style={{ marginTop: 14 }}>
                    <label>Error Reporting</label>
                    <input
                      type="text"
                      value={settings.ini.errorReporting}
                      onChange={(e) =>
                        patchIni("errorReporting", e.target.value)
                      }
                      disabled={busy}
                    />
                    <p className="card-meta" style={{ marginTop: 6 }}>
                      Controls which PHP errors are reported.
                    </p>
                    <label className="rp-check" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={!settings.block.errorReporting}
                        onChange={(e) =>
                          patchBlock("errorReporting", !e.target.checked)
                        }
                        disabled={busy}
                      />
                      <span>Allow domain override</span>
                    </label>
                  </div>
                </section>
              ) : null}

              {nav === "advanced" ? (
                <section className="fpm-sec">
                  <h3>Advanced</h3>
                  <p className="fpm-sec-help">
                    Settings with server-wide impact. Change only if you
                    understand the consequences.
                  </p>
                  <label className="rp-check">
                    <input
                      type="checkbox"
                      checked={settings.runAsNobody}
                      onChange={(e) => patch("runAsNobody", e.target.checked)}
                      disabled={busy}
                    />
                    <span>
                      Run PHP-FPM as nobody{" "}
                      <span className="rp-status rp-status-warn">
                        Security-sensitive
                      </span>
                      <small>
                        When enabled, pool processes run as the nobody user
                        instead of the account user.
                      </small>
                    </span>
                  </label>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="rp-wiz-footer">
        <div className="rp-wiz-footer-meta">
          {dirty
            ? `${changeCount || "Unsaved"} change${
                changeCount === 1 ? "" : "s"
              }`
            : "No unsaved changes"}
        </div>
        <div className="rp-wiz-footer-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy || !dirty}
            onClick={() => {
              try {
                setSettings(JSON.parse(baseline) as FpmSettings);
              } catch {
                void load(username, version);
              }
            }}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            disabled={busy || !dirty}
            onClick={() => void submit()}
          >
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <label className="field">
      <span>{props.label}</span>
      <input
        type="number"
        min={0}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value) || 0)}
        disabled={props.disabled}
      />
    </label>
  );
}

function IniToggle(props: {
  label: string;
  desc: string;
  checked: boolean;
  onChecked: (v: boolean) => void;
  allowOverride: boolean;
  onAllowOverride: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="fpm-ini-row2">
      <div>
        <strong className="mono">{props.label}</strong>
        <span className="faint" style={{ display: "block", fontSize: 12 }}>
          {props.desc}
        </span>
      </div>
      <label className="rp-check" style={{ margin: 0 }}>
        <input
          type="checkbox"
          checked={props.checked}
          onChange={(e) => props.onChecked(e.target.checked)}
          disabled={props.disabled}
        />
        <span>{props.checked ? "Enabled" : "Disabled"}</span>
      </label>
      <label className="rp-check" style={{ margin: 0 }}>
        <input
          type="checkbox"
          checked={props.allowOverride}
          onChange={(e) => props.onAllowOverride(e.target.checked)}
          disabled={props.disabled}
        />
        <span>Allow domain override</span>
      </label>
    </div>
  );
}
